import React from "react"
import { useFormik } from "formik"
import * as Yup from "yup"
import {
  OrderReasonValidation,
  OrderSourceTypeValidation,
  OrderSumValidation,
  OrderTypeOrderValidation,
  Uuid,
  UuidRequired,
} from "../../libs/validation/validation"
import Label from "../Elements/Form/Label"
import Input from "../Elements/Form/Input"
import FormError from "../Elements/Form/FormError"
import Select from "../Elements/Form/Select"
import {
  orderSourceTypeItems,
  orderSourceTypeOthers,
  orderSourceTypeSales,
  orderSourceTypeShopPaymentPoints,
  orderSourceTypeShops,
  orderSourceTypeShopSalePoints,
  orderSourceTypeUsers,
  orderTypeOrderAdd,
  orderTypeOrderRemove,
} from "../../libs/validation/vars"
import {
  castOrderSourceTypeToText,
  castOrderTypeToText,
} from "../../libs/helpers/order"
import OrderSelectShopPaymentPointToViewList from "./OrderSelectShopPaymentPointToViewList"
import { RequestOrderCreateOne } from "../../libs/api/backend/src"

const validationSchema = Yup.object({
  typeOrder: OrderTypeOrderValidation,
  reason: OrderReasonValidation,
  sum: OrderSumValidation,
  sourceType: OrderSourceTypeValidation,
  sourceId: Uuid,
  shopId: UuidRequired,
  shopSalePointId: Uuid,
  shopPaymentPointId: Uuid,
  parentId: Uuid,
})

const initialValuesEmpty = {
  typeOrder: orderTypeOrderAdd,
  reason: "",
  sum: "",
  sourceType: orderSourceTypeShops,
  sourceId: "",
  shopId: "",
  shopSalePointId: "",
  shopPaymentPointId: "",
  parentId: "",
} as RequestOrderCreateOne

interface Props {
  onSubmit: any
  onCancel: any
  values?: RequestOrderCreateOne
  shopId?: string
  shopSalePointId?: string
  shopPaymentPointId?: string
}

export default function OrderFormCreateOrUpdate({
  onSubmit,
  onCancel,
  values = initialValuesEmpty,
  shopId,
  shopSalePointId,
  shopPaymentPointId,
}: Props) {
  if (shopId && !values.shopId) {
    values.shopId = shopId
  }
  if (shopSalePointId && !values.shopSalePointId) {
    values.shopSalePointId = shopSalePointId
  }
  if (shopPaymentPointId && !values.shopPaymentPointId) {
    values.shopPaymentPointId = shopPaymentPointId
  }

  const formik = useFormik({
    initialValues: values,
    validationSchema: validationSchema,
    onSubmit: onSubmit,
  })

  const onSetValue = (fieldName: string, value: any) => {
    formik.setFieldValue(fieldName, value, true)
  }

  return (
    <form
      onSubmit={formik.handleSubmit}
      className="space-y-8 divide-y divide-gray-200"
    >
      <div className="space-y-8 divide-y divide-gray-200 sm:space-y-5">
        <div className="col-span-6 p-5 sm:col-span-6">
          <Label
            htmlFor={"typeOrder"}
            isError={formik.errors.typeOrder && formik.touched.typeOrder}
          >
            Тип
          </Label>
          <Select
            name="typeOrder"
            value={formik.values.typeOrder}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            withLabel={true}
            isError={formik.errors.typeOrder && formik.touched.typeOrder}
            keyId={"id"}
            keyTitle={"title"}
            values={[
              {
                id: orderTypeOrderAdd,
                title: castOrderTypeToText(orderTypeOrderAdd),
              },
              {
                id: orderTypeOrderRemove,
                title: castOrderTypeToText(orderTypeOrderRemove),
              },
            ]}
          />
          <FormError
            isError={formik.errors.typeOrder && formik.touched.typeOrder}
          >
            {formik.errors.typeOrder}
          </FormError>
        </div>

        {formik.values.shopId && formik.values.shopId != "" ? (
          <div className="col-span-6 hidden p-5 sm:col-span-6">
            <Label
              htmlFor={"shopId"}
              isError={formik.errors.shopId && formik.touched.shopId}
            >
              Магазин
            </Label>
            <Input
              type={"text"}
              name={"shopId"}
              defaultValue={formik.values.shopId}
              isDisabled={true}
            />

            <FormError isError={formik.errors.shopId && formik.touched.shopId}>
              {formik.errors.shopId}
            </FormError>
          </div>
        ) : null}

        {formik.values.shopSalePointId &&
        formik.values.shopSalePointId != "" ? (
          <div className="col-span-6 hidden p-5 sm:col-span-6">
            <Label
              htmlFor={"shopSalePointId"}
              isError={
                formik.errors.shopSalePointId && formik.touched.shopSalePointId
              }
            >
              Точка продаж
            </Label>
            <Input
              type={"text"}
              name={"shopSalePointId"}
              defaultValue={formik.values.shopSalePointId}
              isDisabled={true}
            />

            <FormError
              isError={
                formik.errors.shopSalePointId && formik.touched.shopSalePointId
              }
            >
              {formik.errors.shopSalePointId}
            </FormError>
          </div>
        ) : null}

        {formik.values.shopSalePointId &&
        formik.values.shopSalePointId != "" ? (
          <div className="col-span-6 p-5 sm:col-span-6">
            <Label
              htmlFor={"shopPaymentPointId"}
              isError={
                formik.errors.shopPaymentPointId &&
                formik.touched.shopPaymentPointId
              }
            >
              Метод оплаты
            </Label>
            <OrderSelectShopPaymentPointToViewList
              shopSalePointId={formik.values.shopSalePointId}
              value={formik.values.shopPaymentPointId}
              handleChange={(shopPaymentPointId) =>
                onSetValue("shopPaymentPointId", shopPaymentPointId)
              }
            />

            <FormError
              isError={
                formik.errors.shopPaymentPointId &&
                formik.touched.shopPaymentPointId
              }
            >
              {formik.errors.shopPaymentPointId}
            </FormError>
          </div>
        ) : null}

        <div className="col-span-6 p-5 sm:col-span-6">
          <Label
            htmlFor={"reason"}
            isError={formik.errors.reason && formik.touched.reason}
          >
            Описание
          </Label>
          <Input
            type="text"
            name="reason"
            value={formik.values.reason}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            withLabel={true}
            isError={formik.errors.reason && formik.touched.reason}
          />
          <FormError isError={formik.errors.reason && formik.touched.reason}>
            {formik.errors.reason}
          </FormError>
        </div>

        <div className="col-span-6 p-5 sm:col-span-6">
          <Label
            htmlFor={"sum"}
            isError={formik.errors.sum && formik.touched.sum}
          >
            Сумма
          </Label>
          <Input
            type="text"
            name="sum"
            value={formik.values.sum}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            withLabel={true}
            isError={formik.errors.sum && formik.touched.sum}
          />
          <FormError isError={formik.errors.sum && formik.touched.sum}>
            {formik.errors.sum}
          </FormError>
        </div>

        <div className="col-span-6 p-5 sm:col-span-6">
          <Label
            htmlFor={"sourceType"}
            isError={formik.errors.sourceType && formik.touched.sourceType}
          >
            Категория
          </Label>
          <Select
            name="sourceType"
            value={formik.values.sourceType}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            withLabel={true}
            isError={formik.errors.sourceType && formik.touched.sourceType}
            keyId={"id"}
            keyTitle={"title"}
            values={[
              {
                id: orderSourceTypeShops,
                title: castOrderSourceTypeToText(orderSourceTypeShops),
              },
              {
                id: orderSourceTypeItems,
                title: castOrderSourceTypeToText(orderSourceTypeItems),
              },
              {
                id: orderSourceTypeUsers,
                title: castOrderSourceTypeToText(orderSourceTypeUsers),
              },
              {
                id: orderSourceTypeSales,
                title: castOrderSourceTypeToText(orderSourceTypeSales),
              },
              {
                id: orderSourceTypeShopSalePoints,
                title: castOrderSourceTypeToText(orderSourceTypeShopSalePoints),
              },
              {
                id: orderSourceTypeShopPaymentPoints,
                title: castOrderSourceTypeToText(
                  orderSourceTypeShopPaymentPoints
                ),
              },
              {
                id: orderSourceTypeOthers,
                title: castOrderSourceTypeToText(orderSourceTypeOthers),
              },
            ]}
          />
          <FormError
            isError={formik.errors.sourceType && formik.touched.sourceType}
          >
            {formik.errors.sourceType}
          </FormError>
        </div>

        {/*
        <div className="col-span-6 p-5 sm:col-span-6">
          <Label
            htmlFor={"sourceId"}
            isError={formik.errors.sourceId && formik.touched.sourceId}
          >
            ID источника (необязательно)
          </Label>
          <Input
            type="text"
            name="sourceId"
            value={formik.values.sourceId}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            withLabel={true}
            isError={formik.errors.sourceId && formik.touched.sourceId}
          />
          <FormError
            isError={formik.errors.sourceId && formik.touched.sourceId}
          >
            {formik.errors.sourceId}
          </FormError>
        </div>

        <div className="col-span-6 p-5 sm:col-span-6">
          <Label
            htmlFor={"shopSalePointId"}
            isError={
              formik.errors.shopSalePointId && formik.touched.shopSalePointId
            }
          >
            ID точки продаж (необязательно)
          </Label>
          <Input
            type="text"
            name="shopSalePointId"
            value={formik.values.shopSalePointId}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            withLabel={true}
            isError={
              formik.errors.shopSalePointId && formik.touched.shopSalePointId
            }
          />
          <FormError
            isError={
              formik.errors.shopSalePointId && formik.touched.shopSalePointId
            }
          >
            {formik.errors.shopSalePointId}
          </FormError>
        </div>

        <div className="col-span-6 p-5 sm:col-span-6">
          <Label
            htmlFor={"shopPaymentPointId"}
            isError={
              formik.errors.shopPaymentPointId &&
              formik.touched.shopPaymentPointId
            }
          >
            ID метода оплаты (необязательно)
          </Label>
          <Input
            type="text"
            name="shopPaymentPointId"
            value={formik.values.shopPaymentPointId}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            withLabel={true}
            isError={
              formik.errors.shopPaymentPointId &&
              formik.touched.shopPaymentPointId
            }
          />
          <FormError
            isError={
              formik.errors.shopPaymentPointId &&
              formik.touched.shopPaymentPointId
            }
          >
            {formik.errors.shopPaymentPointId}
          </FormError>
        </div>

        <div className="col-span-6 p-5 sm:col-span-6">
          <Label
            htmlFor={"parentId"}
            isError={formik.errors.parentId && formik.touched.parentId}
          >
            ID родителя (необязательно)
          </Label>
          <Input
            type="text"
            name="parentId"
            value={formik.values.parentId}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            withLabel={true}
            isError={formik.errors.parentId && formik.touched.parentId}
          />
          <FormError
            isError={formik.errors.parentId && formik.touched.parentId}
          >
            {formik.errors.parentId}
          </FormError>
        </div>
        */}
      </div>

      <div className="p-5">
        <div className="flex justify-end">
          <button
            type="button"
            disabled={formik.isSubmitting}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:outline-none"
            onClick={(e) => onCancel(e)}
          >
            Отмена
          </button>
          <button
            type="submit"
            disabled={formik.isSubmitting}
            className="ml-3 inline-flex justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:outline-none"
          >
            Сохранить
          </button>
        </div>
      </div>
    </form>
  )
}
